class ProductMainV2 extends HTMLElement {
  constructor() {
    super();

    this.galleryEl = this.querySelector(".js-gallery");
    this.galleryThumbsEl = this.querySelector(".js-gallery-thumbs");
    this.galleryNavBtnLeft = this.querySelector(".js-gallery-nav-btn-left");
    this.galleryNavBtnRight = this.querySelector(".js-gallery-nav-btn-right");
    this.atcBtn = this.querySelector(".js-main-atc-btn");
    this.variantId = +this.dataset.variantId;
    this.hasOnlyDefaultVariant = !!this.variantId;
    this.totalPriceEl = this.atcBtn.querySelector(".js-total-price");
    this.totalCompareAtPriceEl = this.atcBtn.querySelector(".js-total-compare-at-price");

    this.starRatingEls = this.querySelectorAll(".js-star-rating");

    this.variants = {};

    if (this.hasOnlyDefaultVariant) {
      this.variants[this.variantId] = {
        subtotalPrice: +this.dataset.price,
        quantity: 1,
      };
    }
    this.upsells = {};
    this.subscriptionType = "onetime";
    this.subtotalPrice = 0;
    this.totalQuantity = 0;
    this.minQuantity = +this.dataset.minQuantity;
  }

  connectedCallback() {
    this._initGallery();
    this._initStarRating();
    this._attachEventListeners();
    this._preselectVariantFromUrl();
  }

  _preselectVariantFromUrl() {
    if (this.hasOnlyDefaultVariant) return;

    const variantEls = this.querySelectorAll("product-variant-v2");
    if (!variantEls.length) return;

    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get("variant");

    let targetVariantEl = null;
    if (variantIdFromUrl) {
      targetVariantEl = Array.from(variantEls).find(
        (el) => String(el.dataset.variantId) === String(variantIdFromUrl)
      );
    }
    if (!targetVariantEl) {
      targetVariantEl = Array.from(variantEls).find(
        (el) => el.querySelector(".js-atc-btn")?.getAttribute("aria-disabled") !== "true"
      );
    }
    if (!targetVariantEl) return;

    let attempts = 0;
    const maxAttempts = 100;
    const tryPreselect = () => {
      attempts++;
      if (typeof targetVariantEl.preselect === "function") {
        targetVariantEl.preselect();
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(tryPreselect, 10);
      }
    };
    tryPreselect();
  }

  _initGallery() {
    if (this.galleryThumbsEl) {
      this.galleryThumbsSwiper = new Swiper(this.galleryThumbsEl, {
        slidesPerView: 4,
        spaceBetween: 4,
        breakpoints: {
          768: {
            spaceBetween: 16,
          },
        },
      });
    }

    const swiperOptions = {
      navigation: {
        prevEl: this.galleryNavBtnLeft,
        nextEl: this.galleryNavBtnRight,
      },
      slidesPerView: 1,
      effect: "fade",
    };

    if (this.galleryThumbsSwiper) {
      swiperOptions.thumbs = {
        swiper: this.galleryThumbsSwiper,
      };
    }

    this.gallerySwiper = new Swiper(this.galleryEl, swiperOptions);
  }

  _initStarRating() {
    const okeSection = document.querySelector(".shopify-section:has([data-oke-widget])");

    if (okeSection) {
      this.starRatingEls.forEach((el) => {
        const sectionId = okeSection.id;
        el.href = "#" + sectionId;
      });
    }
  }

  _attachEventListeners() {
    this.addEventListener("variant:quantity-changed", this._handleVariantQuantityChange.bind(this));
    this.addEventListener("upsell:quantity-changed", this._handleUpsellQuantityChange.bind(this));
    document.addEventListener("selling-plan:changed", this._handleSellingPlanChange.bind(this));
    this.atcBtn?.addEventListener("click", this._handleAtcBtnClick.bind(this));
  }

  _handleVariantQuantityChange(e) {
    const { variantId, quantity, subtotalPrice } = e.detail;

    if (quantity === 0) {
      delete this.variants[variantId];
    } else {
      if (!this.variants[variantId]) {
        this.variants[variantId] = {};
      }
      this.variants[variantId].quantity = quantity;
      this.variants[variantId].subtotalPrice = subtotalPrice;
    }

    this._calcQuantityAndPrice();
    this._updateAtcBtn();

    this.dispatchEvent(
      new CustomEvent("product:quantity-changed", {
        detail: {
          quantity: this.totalQuantity,
          subtotalPrice: this.subtotalPrice,
          subscriptionType: this.subscriptionType,
          discounts: this.getDiscounts(this.discountsJSON),
        },
        bubbles: true,
      })
    );
  }

  _handleUpsellQuantityChange(e) {
    const { variantId, quantity, subtotalPrice, discountsJSON, sellingPlanId } = e.detail;

    if (quantity === 0) {
      delete this.upsells[variantId];
    } else {
      if (!this.upsells[variantId]) {
        this.upsells[variantId] = {};
      }
      this.upsells[variantId].quantity = quantity;
      this.upsells[variantId].subtotalPrice = subtotalPrice;
      this.upsells[variantId].discountsJSON = discountsJSON;
      this.upsells[variantId].sellingPlanId = sellingPlanId;
    }

    this._calcQuantityAndPrice();
    this._updateAtcBtn();

    this.dispatchEvent(
      new CustomEvent("product:quantity-changed", {
        detail: {
          quantity: this.totalQuantity,
          subtotalPrice: this.subtotalPrice,
          subscriptionType: this.subscriptionType,
          discounts: this.getDiscounts(this.discountsJSON),
        },
        bubbles: true,
      })
    );
  }

  _calcQuantityAndPrice() {
    let totalQuantity = 0;

    for (const item of Object.values(this.variants)) {
      totalQuantity += item.quantity;
    }

    for (const item of Object.values(this.upsells)) {
      totalQuantity += item.quantity;
    }

    this.totalQuantity = totalQuantity;

    let subtotalPrice = 0;
    let totalPrice = 0;

    for (const item of Object.values(this.variants)) {
      subtotalPrice += item.subtotalPrice;
      totalPrice +=
        (item.subtotalPrice *
          (100 - this.getDiscounts(this.discountsJSON)[this.subscriptionType])) /
        100;
    }

    for (const item of Object.values(this.upsells)) {
      subtotalPrice += item.subtotalPrice;
      totalPrice +=
        (item.subtotalPrice *
          (100 - this.getDiscounts(item.discountsJSON)[this.subscriptionType])) /
        100;
    }

    this.subtotalPrice = subtotalPrice;
    this.totalPrice = Math.floor(totalPrice);
  }

  _handleSellingPlanChange(e) {
    this.sellingPlanId = e.detail.sellingPlanId;
    this.subscriptionType = this.sellingPlanId ? "subscription" : "onetime";

    this._calcQuantityAndPrice();
    this._updateAtcBtn();

    this.dispatchEvent(
      new CustomEvent("product:subscription-type-changed", {
        detail: {
          quantity: this.totalQuantity,
          subtotalPrice: this.subtotalPrice,
          subscriptionType: this.subscriptionType,
          discounts: this.getDiscounts(this.discountsJSON),
        },
        bubbles: true,
      })
    );
  }

  async _handleAtcBtnClick(e) {
    // Ignore double-clicks while an add is in flight, and clicks below the minimum quantity
    if (this._atcInFlight) return;
    if (this.atcBtn?.getAttribute("aria-disabled") === "true") return;
    this._atcInFlight = true;
    this.atcBtn?.setAttribute("aria-busy", "true");

    try {
      const items = [...Object.entries(this.variants), ...Object.entries(this.upsells)].map(
        ([key, value]) => ({
          id: key,
          quantity: value.quantity,
          selling_plan:
            this.subscriptionType == "subscription"
              ? value.sellingPlanId ?? this.sellingPlanId
              : null,
        })
      );

      const formData = {
        items: items,
      };

      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const json = await response.json();
    } catch (error) {
      console.error("Error:", error);
    } finally {
      this._atcInFlight = false;
      this.atcBtn?.removeAttribute("aria-busy");
    }
  }

  _updateAtcBtn() {
    const totalCompareAtPrice = this.subtotalPrice;
    const totalPrice = this.totalPrice;

    if(this.totalQuantity < this.minQuantity) {
      this.totalCompareAtPriceEl.innerHTML = "";
      this.totalPriceEl.innerHTML = "";
    } else {
      this.totalCompareAtPriceEl.innerHTML =
        totalCompareAtPrice > totalPrice ? Shopify.formatMoney(totalCompareAtPrice).trim() : "";
      this.totalPriceEl.innerHTML = totalPrice ? Shopify.formatMoney(totalPrice).trim() : "";
    }

    if (!this.hasOnlyDefaultVariant) {
      this.atcBtn?.setAttribute("aria-disabled", this.totalQuantity < this.minQuantity);
    }
  }

  getDiscounts(discountsJSON) {
    const result = {
      subscription: 0,
      onetime: 0,
    };

    ["subscription", "onetime"].forEach((type) => {
      const discounts = discountsJSON[type];

      if (!discounts) return;

      const quantities = Object.keys(discounts)
        .map(Number)
        .sort((a, b) => a - b);

      let selectedQuantity = quantities[0];
      for (const qty of quantities) {
        if (this.totalQuantity >= qty) {
          selectedQuantity = qty;
        } else {
          break;
        }
      }

      result[type] = discounts[selectedQuantity];
    });

    return result;
  }

  get discountsJSON() {
    if (!this._discountsJSON) {
      this._discountsJSON = JSON.parse(this.querySelector(".js-discounts-json").innerHTML);
    }

    return this._discountsJSON;
  }
}

class ProductQuantityMilestonesV2 extends HTMLElement {
  constructor() {
    super();

    this.milestoneEls = this.querySelectorAll(".js-milestone");
    this.milestonePriceEls = this.querySelectorAll(".js-milestone-price");
  }

  connectedCallback() {
    this._attachEventListeners();
  }

  _attachEventListeners() {
    document.addEventListener(
      "product:subscription-type-changed",
      this._handleSubscriptionTypeChange.bind(this)
    );
    document.addEventListener(
      "product:quantity-changed",
      this._handleProductQuantityChange.bind(this)
    );
  }

  _handleSubscriptionTypeChange(e) {
    const newType = e.detail.subscriptionType;

    this.milestonePriceEls.forEach((el) => (el.textContent = el.dataset[`${newType}Price`]));
  }

  _handleProductQuantityChange(e) {
    const newQuantity = e.detail.quantity;

    this.milestoneEls.forEach((el) => {
      el.setAttribute("aria-checked", newQuantity >= +el.dataset.quantity);
    });
  }
}

class ProductVariantV2 extends HTMLElement {
  constructor() {
    super();

    this.atcBtn = this.querySelector(".js-atc-btn");
    this.quantityInputContainer = this.querySelector(".js-quantity-input-container");
    this.quantityDecreaseBtn = this.querySelector(".js-quantity-decrease");
    this.quantityIncreaseBtn = this.querySelector(".js-quantity-increase");
    this.quantityInput = this.querySelector(".js-quantity-input");
    this.variantId = +this.dataset.variantId;
    this.priceEl = this.querySelector(".js-price");
    this.discount = +this.dataset.discount;
    this.originalPrice = +this.dataset.originalPrice;
  }

  connectedCallback() {
    this._attachEventListeners();
  }

  preselect() {
    this.atcBtn.click();
  }

  _attachEventListeners() {
    this.atcBtn.addEventListener("click", this._handleAtcButtonClick.bind(this));
    this.quantityDecreaseBtn.addEventListener(
      "click",
      this._handleQuantityDecreaseBtnClick.bind(this)
    );
    this.quantityIncreaseBtn.addEventListener(
      "click",
      this._handleQuantityIncreaseBtnClick.bind(this)
    );
    document.addEventListener(
      "product:quantity-changed",
      this._handleProductQuantityChange.bind(this)
    );
    document.addEventListener(
      "product:subscription-type-changed",
      this._handleSubscriptionTypeChange.bind(this)
    );
  }

  _handleAtcButtonClick() {
    this.quantityInput.value = 1;
    this.atcBtn.ariaHidden = true;
    this.quantityInputContainer.ariaHidden = false;
    this._checkQuantity();
    this._onQuantityChanged();
  }

  _handleQuantityIncreaseBtnClick() {
    this.quantityInput.value = this.quantityInput.valueAsNumber + 1;
    this.atcBtn.ariaHidden = true;
    this.quantityInputContainer.ariaHidden = false;
    this._checkQuantity();
    this._onQuantityChanged();
  }

  _handleQuantityDecreaseBtnClick() {
    this.quantityInput.value = this.quantityInput.valueAsNumber - 1;
    this.atcBtn.ariaHidden = true;
    this.quantityInputContainer.ariaHidden = false;
    this._checkQuantity();
    this._onQuantityChanged();
  }

  _checkQuantity() {
    const currentValue = parseInt(this.quantityInput.value) || 0;
    const min = parseInt(this.quantityInput.min) || 0;
    const max = parseInt(this.quantityInput.max) || Infinity;

    // Disable decrease button if at or below minimum
    if (currentValue <= min) {
      this.quantityDecreaseBtn.setAttribute("aria-disabled", "true");
      this.quantityInputContainer.ariaHidden = true;
      this.atcBtn.ariaHidden = false;
    } else {
      this.quantityDecreaseBtn.setAttribute("aria-disabled", "false");
      this.quantityInputContainer.ariaHidden = false;
      this.atcBtn.ariaHidden = true;
    }

    // Disable increase button if at or above maximum
    if (currentValue >= max) {
      this.quantityIncreaseBtn.setAttribute("aria-disabled", "true");
    } else {
      this.quantityIncreaseBtn.setAttribute("aria-disabled", "false");
    }
  }

  _onQuantityChanged() {
    const quantity = this.quantityInput.valueAsNumber;
    const subtotalPrice = quantity * this.originalPrice;

    this.dispatchEvent(
      new CustomEvent("variant:quantity-changed", {
        detail: { variantId: this.variantId, quantity: quantity, subtotalPrice: subtotalPrice },
        bubbles: true,
      })
    );
  }

  _handleProductQuantityChange(e) {
    const { discounts, subscriptionType } = e.detail;
    this.discount = discounts[subscriptionType] ?? 0;
    this._updatePrice();
  }

  _handleSubscriptionTypeChange(e) {
    const { discounts, subscriptionType } = e.detail;
    this.discount = discounts[subscriptionType] ?? 0;
    this._updatePrice();
  }

  _updatePrice() {
    this.priceEl.innerHTML = Shopify.formatMoney(
      Math.floor((this.originalPrice * (100 - this.discount)) / 100)
    ).trim();
  }
}

class ProductRechargeWidgetV2 extends HTMLElement {
  constructor() {
    super();

    this.typeRadios = this.querySelectorAll(".js-type-radio");
    this.sellingPlanRadios = this.querySelectorAll(".js-selling-plan-radio");
    this.subscriptionCompareAtPriceEl = this.querySelector(".js-subscription-compare-at-price");
    this.subscriptionPriceEl = this.querySelector(".js-subscription-price");
    this.onetimeCompareAtPriceEl = this.querySelector(".js-onetime-compare-at-price");
    this.onetimePriceEl = this.querySelector(".js-onetime-price");
    this.subscriptionDiscountEls = this.querySelectorAll(".js-subscription-discount");

    this.giftsContainerEl = this.querySelector(".js-gifts-container");
    this.giftEls = this.querySelectorAll(".js-gift");

    const sellingPlanRadio = this.sellingPlanRadios[0];
    this.type = "subscription";
    this.sellingPlanId = sellingPlanRadio ? +sellingPlanRadio.value : +this.dataset.sellingPlanId;
    this.quantity = 0;
  }

  connectedCallback() {
    this._attachEventListeners();

    this.dispatchEvent(
      new CustomEvent("selling-plan:changed", {
        detail: {
          sellingPlanId: this.sellingPlanId,
        },
        bubbles: true,
      })
    );
  }

  _attachEventListeners() {
    this.typeRadios.forEach((radio) =>
      radio.addEventListener("change", this._handleTypeChange.bind(this))
    );
    this.sellingPlanRadios.forEach((radio) =>
      radio.addEventListener("change", this._handleSellingPlanChange.bind(this))
    );
    document.addEventListener(
      "product:quantity-changed",
      this._handleProductQuantityChange.bind(this)
    );
  }

  _handleTypeChange(e) {
    this.type = e.target.value;

    this._updateGifts();

    this.dispatchEvent(
      new CustomEvent("selling-plan:changed", {
        detail: {
          sellingPlanId: this.type === "subscription" ? this.sellingPlanId : null,
        },
        bubbles: true,
      })
    );
  }

  _handleSellingPlanChange(e) {
    this.sellingPlanId = +e.target.value;

    this._updateGifts();

    this.dispatchEvent(
      new CustomEvent("selling-plan:changed", {
        detail: {
          sellingPlanId: this.type === "subscription" ? this.sellingPlanId : null,
        },
        bubbles: true,
      })
    );
  }

  _handleProductQuantityChange(e) {
    const { quantity, discounts, subtotalPrice } = e.detail;

    this.quantity = quantity;

    const subscriptionCompareAtPrice =
      quantity > 0 ? subtotalPrice : +this.subscriptionCompareAtPriceEl.dataset.originalPrice;
    const subscriptionPrice =
      quantity > 0
        ? Math.floor((subtotalPrice * (100 - discounts.subscription)) / 100)
        : +this.subscriptionPriceEl.dataset.originalPrice;
    const onetimeCompareAtPrice =
      quantity > 0 ? subtotalPrice : +this.onetimeCompareAtPriceEl.dataset.originalPrice;
    const onetimePrice =
      quantity > 0
        ? Math.floor((subtotalPrice * (100 - discounts.onetime)) / 100)
        : +this.onetimePriceEl.dataset.originalPrice;

    if (subscriptionCompareAtPrice > subscriptionPrice) {
      this.subscriptionCompareAtPriceEl.ariaHidden = false;
      this.subscriptionCompareAtPriceEl.textContent = Shopify.formatMoney(
        subscriptionCompareAtPrice
      );
    } else {
      this.subscriptionCompareAtPriceEl.ariaHidden = true;
    }

    this.subscriptionPriceEl.textContent = Shopify.formatMoney(subscriptionPrice);

    if (onetimeCompareAtPrice > onetimePrice) {
      this.onetimeCompareAtPriceEl.ariaHidden = false;
      this.onetimeCompareAtPriceEl.textContent = Shopify.formatMoney(onetimeCompareAtPrice);
    } else {
      this.onetimeCompareAtPriceEl.ariaHidden = true;
    }

    this.onetimePriceEl.textContent = Shopify.formatMoney(onetimePrice);

    this.subscriptionDiscountEls.forEach(
      (el) => (el.textContent = discounts.subscription > 0 ? Math.floor(discounts.subscription) + "%" : "")
    );

    this._updateGifts();
  }

  _updateGifts() {
    this.giftsContainerEl?.setAttribute("aria-expanded", this.type === "subscription");
    this.giftEls.forEach((el) =>
      el.setAttribute(
        "aria-hidden",
        +el.dataset.quantity !== 1 && +el.dataset.quantity > this.quantity
      )
    );
  }
}

class ProductUpsellV2 extends HTMLElement {
  constructor() {
    super();

    this.atcBtn = this.querySelector(".js-atc-btn");
    this.quantityInputContainer = this.querySelector(".js-quantity-input-container");
    this.quantityDecreaseBtn = this.querySelector(".js-quantity-decrease");
    this.quantityIncreaseBtn = this.querySelector(".js-quantity-increase");
    this.quantityInput = this.querySelector(".js-quantity-input");
    this.variantId = +this.dataset.variantId;
    this.sellingPlanId = +this.dataset.sellingPlanId;
    this.priceEl = this.querySelector(".js-price");
    this.discount = +this.dataset.discount;
    this.originalPrice = +this.dataset.originalPrice;
    this.productQuantity = 0;
    this.subscriptionType = "onetime";
  }

  connectedCallback() {
    this._attachEventListeners();
  }

  _attachEventListeners() {
    this.atcBtn.addEventListener("click", this._handleAtcButtonClick.bind(this));
    this.quantityDecreaseBtn.addEventListener(
      "click",
      this._handleQuantityDecreaseBtnClick.bind(this)
    );
    this.quantityIncreaseBtn.addEventListener(
      "click",
      this._handleQuantityIncreaseBtnClick.bind(this)
    );
    document.addEventListener(
      "product:quantity-changed",
      this._handleProductQuantityChange.bind(this)
    );
    document.addEventListener(
      "product:subscription-type-changed",
      this._handleSubscriptionTypeChange.bind(this)
    );
  }

  _handleAtcButtonClick() {
    this.quantityInput.value = 1;
    this.atcBtn.ariaHidden = true;
    this.quantityInputContainer.ariaHidden = false;
    this._checkQuantity();
    this._onQuantityChanged();
  }

  _handleQuantityIncreaseBtnClick() {
    this.quantityInput.value = this.quantityInput.valueAsNumber + 1;
    this.atcBtn.ariaHidden = true;
    this.quantityInputContainer.ariaHidden = false;
    this._checkQuantity();
    this._onQuantityChanged();
  }

  _handleQuantityDecreaseBtnClick() {
    this.quantityInput.value = this.quantityInput.valueAsNumber - 1;
    this.atcBtn.ariaHidden = true;
    this.quantityInputContainer.ariaHidden = false;
    this._checkQuantity();
    this._onQuantityChanged();
  }

  _checkQuantity() {
    const currentValue = parseInt(this.quantityInput.value) || 0;
    const min = parseInt(this.quantityInput.min) || 0;
    const max = parseInt(this.quantityInput.max) || Infinity;

    // Disable decrease button if at or below minimum
    if (currentValue <= min) {
      this.quantityDecreaseBtn.setAttribute("aria-disabled", "true");
      this.quantityInputContainer.ariaHidden = true;
      this.atcBtn.ariaHidden = false;
    } else {
      this.quantityDecreaseBtn.setAttribute("aria-disabled", "false");
      this.quantityInputContainer.ariaHidden = false;
      this.atcBtn.ariaHidden = true;
    }

    // Disable increase button if at or above maximum
    if (currentValue >= max) {
      this.quantityIncreaseBtn.setAttribute("aria-disabled", "true");
    } else {
      this.quantityIncreaseBtn.setAttribute("aria-disabled", "false");
    }
  }

  _onQuantityChanged() {
    const quantity = this.quantityInput.valueAsNumber;
    const subtotalPrice = quantity * this.originalPrice;

    this.dispatchEvent(
      new CustomEvent("upsell:quantity-changed", {
        detail: {
          variantId: this.variantId,
          quantity: quantity,
          subtotalPrice: subtotalPrice,
          discountsJSON: this.discountsJSON,
          sellingPlanId: this.sellingPlanId,
        },
        bubbles: true,
      })
    );
  }

  _handleProductQuantityChange(e) {
    const { quantity } = e.detail;
    this.productQuantity = quantity;
    this.discount = this.discounts[this.subscriptionType] ?? 0;
    this._updatePrice();
  }

  _handleSubscriptionTypeChange(e) {
    const { subscriptionType } = e.detail;
    this.subscriptionType = subscriptionType;
    this.discount = this.discounts[this.subscriptionType] ?? 0;
    this._updatePrice();
  }

  _updatePrice() {
    this.priceEl.innerHTML = Shopify.formatMoney(
      Math.floor((this.originalPrice * (100 - this.discount)) / 100)
    ).trim();
  }

  get discounts() {
    const result = {
      subscription: 0,
      onetime: 0,
    };

    ["subscription", "onetime"].forEach((type) => {
      const discounts = this.discountsJSON[type];

      if (!discounts) return;

      const quantities = Object.keys(discounts)
        .map(Number)
        .sort((a, b) => a - b);

      let selectedQuantity = quantities[0];
      for (const qty of quantities) {
        if (this.productQuantity >= qty) {
          selectedQuantity = qty;
        } else {
          break;
        }
      }

      result[type] = discounts[selectedQuantity];
    });

    return result;
  }

  get discountsJSON() {
    if (!this._discountsJSON) {
      this._discountsJSON = JSON.parse(this.querySelector(".js-upsell-discounts-json").innerHTML);
    }

    return this._discountsJSON;
  }
}

class ProductLightboxV2 extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.image = null;
  }

  connectedCallback() {
    const img = this.querySelector(".js-img");
    if (img) {
      img.style.cursor = "pointer";
      img.addEventListener("click", () => this.open());
    }
  }

  open() {
    const img = this.querySelector(".js-img");
    if (!img) return;

    // Create modal
    this.modal = document.createElement("div");
    this.modal.className = "product-lightbox-modal";

    // Create close button
    const closeBtn = document.createElement("button");
    closeBtn.className = "product-lightbox-close";
    closeBtn.innerHTML = "&times;";
    closeBtn.addEventListener("click", () => this.close());

    // Create image
    this.image = document.createElement("img");
    this.image.src = img.src.replace("width=400", "width=1200");
    this.image.alt = img.alt;
    this.image.className = "product-lightbox-image";

    // Append elements
    this.modal.appendChild(closeBtn);
    this.modal.appendChild(this.image);
    document.body.appendChild(this.modal);

    // Close on background click
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.close();
    });

    // Close on ESC key
    this.handleKeydown = (e) => {
      if (e.key === "Escape") this.close();
    };
    document.addEventListener("keydown", this.handleKeydown);

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Fade in
    requestAnimationFrame(() => {
      this.modal.classList.add("active");
    });
  }

  close() {
    if (!this.modal) return;

    this.modal.classList.remove("active");

    setTimeout(() => {
      if (this.modal) {
        this.modal.remove();
        this.modal = null;
      }
      document.body.style.overflow = "";
      document.removeEventListener("keydown", this.handleKeydown);
    }, 300);
  }
}

if(!customElements.get("product-main-v2")) {
  customElements.define("product-lightbox-v2", ProductLightboxV2);
  customElements.define("product-main-v2", ProductMainV2);
  customElements.define("product-quantity-milestones-v2", ProductQuantityMilestonesV2);
  customElements.define("product-variant-v2", ProductVariantV2);
  customElements.define("product-upsell-v2", ProductUpsellV2);
  customElements.define("product-recharge-widget-v2", ProductRechargeWidgetV2);
}

